import axios from 'axios'
import { getToken, clearToken, login } from '../auth/sso.js'

const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase())

const keysToCamel = (obj) => {
  if (Array.isArray(obj)) return obj.map(keysToCamel)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toCamel(k), keysToCamel(v)])
    )
  }
  return obj
}

// Fields the server stores as jsonb but serializes as JSON strings — parse them back to objects
const JSON_FIELDS = new Set(['agentYaml', 'memoryState', 'payload', 'details'])

const parseJsonFields = (obj) => {
  if (Array.isArray(obj)) return obj.map(parseJsonFields)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => {
        if (JSON_FIELDS.has(k) && typeof v === 'string') {
          try { return [k, JSON.parse(v)] } catch (_) { return [k, v] }
        }
        return [k, parseJsonFields(v)]
      })
    )
  }
  return obj
}

const client = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Every call carries the SSO token; the server rejects /api/** without it.
client.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

client.interceptors.response.use(
  (response) => {
    response.data = parseJsonFields(keysToCamel(response.data))
    return response
  },
  (error) => {
    // A rejected token is unrecoverable in-page: drop it and re-run the SSO
    // handshake rather than surfacing a misleading "request failed".
    if (error.response?.status === 401) {
      clearToken()
      login()
      return Promise.reject(new Error('Session expired — signing in again'))
    }
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Request failed'
    return Promise.reject(new Error(message))
  }
)

// Workspaces
export const listWorkspaces = () =>
  client.get('/workspaces').then((r) => r.data)

export const createWorkspace = (payload) =>
  client.post('/workspaces', payload).then((r) => r.data)

export const getWorkspace = (id) =>
  client.get(`/workspaces/${id}`).then((r) => r.data)

export const deleteWorkspace = (id) =>
  client.delete(`/workspaces/${id}`).then((r) => r.data)

// Snapshots
export const listSnapshots = (workspaceId) =>
  client.get(`/workspaces/${workspaceId}/snapshots`).then((r) => r.data)

export const createSnapshot = (workspaceId, payload) =>
  client.post(`/workspaces/${workspaceId}/snapshots`, payload).then((r) => r.data)

export const rollbackSnapshot = (workspaceId, snapshotId) =>
  client.post(`/workspaces/${workspaceId}/rollback/${snapshotId}`).then((r) => r.data)

// Evals
export const listEvals = (snapshotId) =>
  client.get(`/snapshots/${snapshotId}/evals`).then((r) => r.data)

// Telemetry
export const listTelemetry = (workspaceId, limit = 100) =>
  client.get('/telemetry', { params: { workspaceId, limit } }).then((r) => r.data)

// Agent control (PATCH status, POST actions)
export const pauseAgent = (workspaceId) =>
  client.patch(`/workspaces/${workspaceId}`, { status: 'PAUSED' }).then((r) => r.data)

export const resumeAgent = (workspaceId) =>
  client.patch(`/workspaces/${workspaceId}`, { status: 'ACTIVE' }).then((r) => r.data)

export const terminateAgent = (workspaceId) =>
  client.delete(`/workspaces/${workspaceId}`).then((r) => r.data)

export const forceCheckpoint = (workspaceId) =>
  client.post(`/workspaces/${workspaceId}/snapshots`, { message: 'Force checkpoint' }).then((r) => r.data)

// Snapshot file storage
export const listSnapshotFiles = (workspaceId, snapshotId) =>
  client.get(`/workspaces/${workspaceId}/snapshots/${snapshotId}/files`).then((r) => r.data)

export const getSnapshotFile = (workspaceId, snapshotId, filePath) =>
  client.get(`/workspaces/${workspaceId}/snapshots/${snapshotId}/file`, { params: { path: filePath } })
    .then((r) => r.data)

export const getSnapshotDiff = (workspaceId, snapshotId) =>
  client.get(`/workspaces/${workspaceId}/snapshots/${snapshotId}/diff`).then((r) => r.data)
